import { z } from "zod";
import { VENUS_QWEN38_CYCLE2_LORA_VERDA_MODEL, VENUS_QWEN38_CUSTOM_KERNEL_VERDA_MODEL, VENUS_QWEN38_DFLASH2_VERDA_MODEL, VENUS_QWEN38_SGLANG_NVFP4_DFLASH2_VERDA_MODEL, VENUS_QWEN38_SGLANG_NVFP4_VERDA_MODEL, VENUS_QWEN38_VERDA_MODEL, VENUS_QWEN38_VLLM_NVFP4_DFLASH2_VERDA_MODEL, VENUS_QWEN38_VLLM_NVFP4_VERDA_MODEL, } from "./self-hosted-experiment.js";
export const VENUS_CAPACITY_LEASE_MS = 30 * 60 * 1_000;
export const venusCapacityPhaseSchema = z.enum([
    "idle",
    "requested",
    "allocating",
    "booting",
    "loading_model",
    "ready",
    "deleting",
    "failed",
]);
/** The Verda serving profiles a thirty-minute block can hold. */
export const venusCapacityModelSchema = z.union([
    z.literal(VENUS_QWEN38_VERDA_MODEL),
    z.literal(VENUS_QWEN38_CYCLE2_LORA_VERDA_MODEL),
    z.literal(VENUS_QWEN38_DFLASH2_VERDA_MODEL),
    z.literal(VENUS_QWEN38_CUSTOM_KERNEL_VERDA_MODEL),
    z.literal(VENUS_QWEN38_SGLANG_NVFP4_VERDA_MODEL),
    z.literal(VENUS_QWEN38_SGLANG_NVFP4_DFLASH2_VERDA_MODEL),
    z.literal(VENUS_QWEN38_VLLM_NVFP4_VERDA_MODEL),
    z.literal(VENUS_QWEN38_VLLM_NVFP4_DFLASH2_VERDA_MODEL),
]);
export const venusCapacityStatusSchema = z.object({
    contract: z.literal("mlx.venus.capacity.v1"),
    phase: venusCapacityPhaseSchema,
    requestedAt: z.number().int().nonnegative().nullable(),
    readyAt: z.number().int().nonnegative().nullable(),
    expiresAt: z.number().int().nonnegative().nullable(),
    instanceType: z.literal("1RTXPRO6000.30V"),
    location: z.literal("FIN-02"),
    accelerator: z.literal("RTX PRO 6000"),
    vramGb: z.literal(96),
    pricing: z.literal("spot"),
    model: venusCapacityModelSchema,
    baseModel: z.string().min(1),
    quantization: z.enum(["Q5_K_XL", "FP8 (merged LoRA)", "BF16 + DFlash2", "NVFP4", "NVFP4 + DFlash2"]),
    servingEngine: z.enum(["llama.cpp", "SGLang", "vLLM", "NInfer"]),
    failureCode: z.string().min(1).nullable(),
});
/**
 * The Venus capacity role's route on the tenant API. The role presents its own credential
 * (MLX_VENUS_CAPACITY_SHARED_SECRET) in x-shared-secret and acts on the lease row only while it
 * holds the row's controller lease.
 */
export const VENUS_CAPACITY_CONTROL_PATH = "/api/worker/venus/capacity";
/** How long a claimed controller lease lasts; the holder renews it at half-life. */
export const VENUS_CAPACITY_CONTROLLER_LEASE_MS = 60_000;
const epochMilliseconds = z.number().int().nonnegative();
/** A capacity process: host name, process and nonce, so a restart is a new holder. */
export const venusCapacityHolderSchema = z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/u);
/** A Verda instance ID exactly as the provider returns it. */
export const venusCapacityInstanceIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9-]{2,127}$/u);
/** An inference endpoint on provisioned capacity: HTTPS, one host, the OpenAI-compatible /v1. */
export const venusCapacityEndpointSchema = z.string().regex(/^https:\/\/[^/]+\/v1$/u);
export const venusCapacityFailureCodeSchema = z.string().regex(/^[a-z0-9_]{3,80}$/u);
/**
 * The lease row as the capacity role sees it: unlike the browser's status, it carries the exact
 * provider instance and endpoint the role acts on.
 */
export const venusCapacityLeaseRecordSchema = z.object({
    generation: epochMilliseconds,
    model: venusCapacityModelSchema,
    phase: venusCapacityPhaseSchema,
    requestedAt: epochMilliseconds.nullable(),
    readyAt: epochMilliseconds.nullable(),
    expiresAt: epochMilliseconds.nullable(),
    instanceId: venusCapacityInstanceIdSchema.nullable(),
    endpoint: venusCapacityEndpointSchema.nullable(),
    failureCode: z.string().min(1).max(200).nullable(),
}).strict();
/**
 * One write to the lease row. The endpoint and failure code are always replaced; an absent
 * instance ID or ready time keeps the recorded one.
 */
export const venusCapacityTransitionSchema = z.object({
    phase: venusCapacityPhaseSchema,
    instanceId: venusCapacityInstanceIdSchema.nullable().optional(),
    endpoint: venusCapacityEndpointSchema.nullable(),
    readyAt: epochMilliseconds.optional(),
    failureCode: venusCapacityFailureCodeSchema.nullable(),
}).strict();
const fencedWrite = {
    holder: venusCapacityHolderSchema,
    generation: epochMilliseconds,
    from: z.array(venusCapacityPhaseSchema).min(1),
};
/**
 * What the capacity role asks of the lease row: claim (or renew) the controller lease and read
 * the row; write it, or reset it to idle, only as the current holder, for the generation it
 * read and from the phases it expects; release the lease when it stops.
 */
export const venusCapacityCommandSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("claim"), holder: venusCapacityHolderSchema,
        leaseMs: z.number().int().min(10_000).max(600_000) }).strict(),
    z.object({ action: z.literal("transition"), ...fencedWrite, set: venusCapacityTransitionSchema }).strict(),
    z.object({ action: z.literal("reset"), ...fencedWrite }).strict(),
    z.object({ action: z.literal("release"), holder: venusCapacityHolderSchema }).strict(),
]);
/** The row after a claim or a write the holder was allowed to make. */
export const venusCapacityControlResultSchema = z.object({
    lease: venusCapacityLeaseRecordSchema,
    controllerLeaseExpiresAt: epochMilliseconds,
}).strict();
/** Why a claim or write was refused (409): another holder's lease, or a row that moved on. */
export const VENUS_CAPACITY_FENCED_CODES = ["venus_capacity_controller_fenced", "venus_capacity_transition_conflict"];
